import { useState, useEffect } from "react";
import { getWbSnapshots, getWbSnapshot, takeWbSnapshot } from "../../apiService";
import { ModalWrap } from "../../components/ModalWrap";
import { useToast } from "../../components/Toast";

const fmt2 = n => Number(n).toFixed(2);
const fmt4 = n => Number(n).toFixed(4);

export function PageWbPriceSnapshot() {
  const showToast = useToast();
  const [snapshots,    setSnapshots]    = useState([]);
  const [viewSnap,     setViewSnap]     = useState(null);
  const [showTake,     setShowTake]     = useState(false);
  const [comment,      setComment]      = useState("");
  const [taking,       setTaking]       = useState(false);
  const [loadingView,  setLoadingView]  = useState(false);

  useEffect(() => {
    getWbSnapshots()
      .then(setSnapshots)
      .catch(e => console.error("Failed to load snapshots:", e));
  }, []);

  async function handleTake() {
    if (!comment.trim()) return;
    setTaking(true);
    try {
      const snap = await takeWbSnapshot(comment.trim());
      setSnapshots(prev => [snap, ...prev]);
      setShowTake(false);
      setComment("");
      showToast("Price snapshot taken");
    } catch (e) {
      alert("Failed to take snapshot: " + e.message);
    } finally {
      setTaking(false);
    }
  }

  async function handleView(snap) {
    setLoadingView(true);
    try {
      const full = await getWbSnapshot(snap.id);
      setViewSnap(full);
    } catch (e) {
      alert("Failed to load snapshot: " + e.message);
    } finally {
      setLoadingView(false);
    }
  }

  return (
    <>
      {/* ── Page header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Waterbase Price Snapshots</div>
          <div className="page-subtitle">Cost price history at 3 pack sizes</div>
        </div>
        <div className="page-header-right">
          <button className="btn-primary" onClick={() => setShowTake(true)}>
            Take Snapshot
          </button>
        </div>
      </div>

      {/* ── Snapshot history table ── */}
      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Trigger</th>
                <th>Created by</th>
                <th>Comment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {snapshots.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign:"center", color:"#999", padding:24 }}>
                  No snapshots yet — take the first one using the button above.
                </td></tr>
              )}
              {snapshots.map(snap => (
                <tr key={snap.id}>
                  <td style={{ fontWeight:600 }}>{snap.snapshotDate}</td>
                  <td>
                    {snap.triggerType === "SCHEDULED"
                      ? <span className="badge badge-blue">Scheduled</span>
                      : <span className="badge badge-purple">Manual</span>}
                  </td>
                  <td style={{ color:"#555", fontSize:13 }}>{snap.createdBy}</td>
                  <td style={{ color:"#666", fontSize:13 }}>{snap.comment || <span className="text-muted">—</span>}</td>
                  <td>
                    <button
                      className="btn-small"
                      disabled={loadingView}
                      onClick={() => handleView(snap)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Take Snapshot modal ── */}
      {showTake && (
        <div className="overlay">
          <ModalWrap title="Take Price Snapshot" onClose={() => { setShowTake(false); setComment(""); }}>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Comment <span style={{ color:"#e00" }}>*</span></label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Reason for taking this snapshot…"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  style={{ resize:"vertical" }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => { setShowTake(false); setComment(""); }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={!comment.trim() || taking}
                onClick={handleTake}
              >
                {taking ? "Taking…" : "Take Snapshot"}
              </button>
            </div>
          </ModalWrap>
        </div>
      )}

      {/* ── View snapshot detail modal ── */}
      {viewSnap && (
        <div className="overlay">
          <ModalWrap
            title={`Snapshot — ${viewSnap.snapshotDate}`}
            wide
            onClose={() => setViewSnap(null)}
          >
            <div className="modal-body">
              <div style={{ display:"flex", gap:16, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                {viewSnap.triggerType === "SCHEDULED"
                  ? <span className="badge badge-blue">Scheduled</span>
                  : <span className="badge badge-purple">Manual</span>}
                <span style={{ color:"#666", fontSize:13 }}>{viewSnap.createdBy}</span>
                {viewSnap.comment && (
                  <span style={{ color:"#444", fontSize:13, fontStyle:"italic" }}>"{viewSnap.comment}"</span>
                )}
              </div>

              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Grade</th>
                      <th style={{ textAlign:"right", background:"#f0f7ff", color:"#1e40af" }}>20 Kg (AED)</th>
                      <th style={{ textAlign:"right", background:"#f0f7ff", color:"#1e40af" }}>per kg</th>
                      <th style={{ textAlign:"right", background:"#f0fdf4", color:"#166534" }}>200 Kg (AED)</th>
                      <th style={{ textAlign:"right", background:"#f0fdf4", color:"#166534" }}>per kg</th>
                      <th style={{ textAlign:"right", background:"#fffbeb", color:"#92400e" }}>1000 Kg (AED)</th>
                      <th style={{ textAlign:"right", background:"#fffbeb", color:"#92400e" }}>per kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewSnap.lines || []).map(line => (
                      <tr key={line.fgId}>
                        <td className="fw-600">{line.fgName}</td>
                        <td style={{ textAlign:"right" }}>{fmt2(line.total20kg)}</td>
                        <td style={{ textAlign:"right", color:"#666", fontSize:12 }}>{fmt4(line.total20kg / 20)}</td>
                        <td style={{ textAlign:"right" }}>{fmt2(line.total200kg)}</td>
                        <td style={{ textAlign:"right", color:"#666", fontSize:12 }}>{fmt4(line.total200kg / 200)}</td>
                        <td style={{ textAlign:"right" }}>{fmt2(line.total1000kg)}</td>
                        <td style={{ textAlign:"right", color:"#666", fontSize:12 }}>{fmt4(line.total1000kg / 1000)}</td>
                      </tr>
                    ))}
                    {(!viewSnap.lines || viewSnap.lines.length === 0) && (
                      <tr><td colSpan={7} style={{ textAlign:"center", color:"#999" }}>
                        No grade data in this snapshot.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </ModalWrap>
        </div>
      )}
    </>
  );
}
